import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { IPaginationModel } from '~/shared/types/types';

export const usePersistedTableState = (
  namespace: string,
  defaultPaginationModel: IPaginationModel
) => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Filters
  const [activeFilters, setActiveFilters] = useState<Record<string, any> | null>(() => {
    const filtersParam = searchParams.get('filters');
    if (filtersParam) {
      try {
        return JSON.parse(decodeURIComponent(filtersParam));
      } catch (e) {
        console.error('Failed to parse filters from URL', e);
        return null;
      }
    }
    const storedFilters = localStorage.getItem(`${namespace}_activeFilters`);
    if (storedFilters) {
      try {
        return JSON.parse(storedFilters);
      } catch (e) {
        console.error('Failed to parse filters from localStorage', e);
        return null;
      }
    }
    return null;
  });

  // Sort
  const [sortModel, setSortModel] = useState<any>(() => {
    const sortParam = searchParams.get('sort');
    if (sortParam) {
      try {
        return JSON.parse(decodeURIComponent(sortParam));
      } catch (e) {
        return [];
      }
    }
    const storedSort = localStorage.getItem(`${namespace}_sortModel`);
    if (storedSort) {
      try {
        return JSON.parse(storedSort);
      } catch (e) {
        return [];
      }
    }
    return [];
  });

  // Pagination
  const [paginationModel, setPaginationModel] = useState<IPaginationModel>(() => {
    const pageParam = searchParams.get('page');
    const pageSizeParam = searchParams.get('pageSize');
    const searchParam = searchParams.get('search');

    if (pageParam || pageSizeParam || searchParam) {
      const page = parseInt(pageParam || '0', 10);
      const pageSize = parseInt(pageSizeParam || defaultPaginationModel.pageSize.toString(), 10);
      const search = searchParam || '';
      return { ...defaultPaginationModel, page, pageSize, search };
    }

    const storedPagination = localStorage.getItem(`${namespace}_paginationModel`);
    if (storedPagination) {
      try {
        return JSON.parse(storedPagination);
      } catch (e) {
        return defaultPaginationModel;
      }
    }

    return defaultPaginationModel;
  });

  // Sync with URL and LocalStorage
  useEffect(() => {
    const params = new URLSearchParams();

    if (paginationModel.page > 0) params.set('page', paginationModel.page.toString());
    if (paginationModel.pageSize !== defaultPaginationModel.pageSize) params.set('pageSize', paginationModel.pageSize.toString());
    if (paginationModel.search) params.set('search', paginationModel.search);

    if (activeFilters) {
      params.set('filters', encodeURIComponent(JSON.stringify(activeFilters)));
      localStorage.setItem(`${namespace}_activeFilters`, JSON.stringify(activeFilters));
    } else {
      localStorage.removeItem(`${namespace}_activeFilters`);
    }

    if (sortModel && sortModel.length > 0) {
      params.set('sort', encodeURIComponent(JSON.stringify(sortModel)));
      localStorage.setItem(`${namespace}_sortModel`, JSON.stringify(sortModel));
    } else {
      localStorage.removeItem(`${namespace}_sortModel`);
    }

    localStorage.setItem(`${namespace}_paginationModel`, JSON.stringify(paginationModel));

    setSearchParams(params, { replace: true });
  }, [paginationModel, activeFilters, sortModel, setSearchParams, namespace, defaultPaginationModel]);

  return {
    activeFilters,
    setActiveFilters,
    sortModel,
    setSortModel,
    paginationModel,
    setPaginationModel,
  };
};
